import { drop, path, omit } from 'ramda';

import { addRequestEncode, addRequestDecode, RequestDecode, RequestCountersMapper } from './requestMapper';
import { formatDate, dateToStr, timeToStr, strToTime, strToDate, decodeBase64Win1251 } from './commonMapper';
import { worksheetSettingsMapper, worksheetMassSettingsMapper, projectSettingsMapper, projectMassSettingsMapper } from './settingsMapper';
import ProjectListMapper, { projectMapper } from './projectMapper';
import { companyMapper } from './companyMapper';
import DictionaryMapper, { competenceMapper } from './dictionaryMapper';
import WorksheetMapper from './worksheetMapper';
import { projectApprovalMapper, personApprovalMapper, applicationApprovalCountersMapper } from './approvalMapper';
import PersonMapper from './personMapper';
import personStateMapper from './personStateMapper';
import EventMapper from './eventMapper';
import evolve, { toNumber, toCode } from './evolve';
import userMapper from './userMapper';
import departmentMapper from './departmentMapper';
import WorksheetMassMapper from './worksheetMassMapper';
import notdoneMapper from './notdoneMapper';
import { toBase64 } from 'helpers/base64Helper';
import timetableMapper from './timetableMapper';
import stagesMapper from './stagesMapper';

const parseToNumber = value => value ? toNumber(value) : value;

const FORMATTERS = [
    {
        pattern: /settings\/worksheet\/mass/,
        decode: worksheetMassSettingsMapper
    },
    {
        pattern: /settings\/worksheet/,
        decode: worksheetSettingsMapper
    },
    {
        pattern: /settings\/project\/mass/,
        decode: projectMassSettingsMapper
    },
    {
        pattern: /settings\/project/,
        decode: projectSettingsMapper
    },
    {
        pattern: /person\/\d+\/project/,
        decode: ProjectListMapper
    },
    {
        pattern: /project\/mass$/,
        decode: ProjectListMapper
    },
    {
        pattern: /project$/,
        decode: evolve({
            project: projectApprovalMapper
        })
    },
    {
        pattern: /approval\/project\/\d+$/,
        decode: projectApprovalMapper
    },
    {
        pattern: /project\/\d+$/,
        decode: projectMapper
    },
    {
        pattern: /project\/mass\/\d+\/detail\/\d+$/,
        decode: projectMapper
    },
    {
        pattern: /project\/request$/,
        encode: addRequestEncode,
        decode: addRequestDecode
    },
    {
        pattern: /project\/request\/\d+$/,
        decode: RequestDecode
    },
    {
        pattern: /company\/\d+$/,
        decode: companyMapper
    },
    {
        pattern: /approval$/,
        decode: evolve({
            approval: personApprovalMapper
        })
    },
    {
        pattern: /approval\/\d+$/,
        decode: personApprovalMapper
    },
    {
        pattern: /calendar\/task(\/\d+)?$/,
        encode: evolve({
            date: {
                to: dateToStr,
                from: dateToStr
            },
            alarm: {
                date: dateToStr,
                time: timeToStr
            },
            priority: (value) => parseInt(value, 10)
        })
    },
    {
        pattern: /calendar\/event(\/\d+)?$/,
        encode: evolve({
            date: dateToStr,
            time: {
                to: timeToStr,
                from: timeToStr
            }
        })
    },
    {
        pattern: /event(\/\d+)?$/,
        encode: evolve({
            contact: {
                type: toNumber,
                person: toNumber,
                project: toNumber,
                employee: toNumber,
                contact: toNumber,
                organization: toNumber,
            },
            details: {
                dateTo: dateToStr,
                time: {
                    to: timeToStr,
                    from: timeToStr,
                },
                personState: parseToNumber,
                endDate: dateToStr,
                workToDate: dateToStr,
                comeToWorkDate: dateToStr,
                salary: parseToNumber,
                valSalary: parseToNumber,
                gonorar: parseToNumber,
                valGonorar: parseToNumber,
                organizationId: parseToNumber,
                departmentId: parseToNumber,
            },
            result: EventMapper.documentResult,
            shortResultBlock: EventMapper.shortResult,
        }),
        decode: evolve({
            details: {
                eventTime: {
                    from: strToTime,
                    to: strToTime
                },
                eventDate: value => strToDate(value),
            },
            shortResult: EventMapper.shortResultDecode,
        })
    },
    {
        pattern: /\d+\/worksheet/,
        decode: WorksheetMapper
    },
    {
        pattern: /person(\/\d+)?$/,
        decode: evolve({
            classifier: { parts: { text: PersonMapper.classifier } },
            workWith: PersonMapper.workWith,
            documents: { date: formatDate },
            bithday: PersonMapper.bithday,
            lastwork: PersonMapper.work,
            workplace: PersonMapper.work,
            phone: PersonMapper.phone,
            email: PersonMapper.email,
            sex: PersonMapper.sex
        })
    },
    {
        pattern: /currency\?/,
        decode: evolve({
            code: DictionaryMapper.currency
        })
    },
    {
        pattern: /project\/request\/counters/,
        decode: RequestCountersMapper
    },
    {
        pattern: /user\/current\/ext/,
        decode: userMapper
    },
    {
        pattern: /language\?/,
        decode: (languages) => drop(1, languages)
    },
    {
        pattern: /document\/\d+\/edit\/text/,
        encode: ({ result, fileType }) => ({
            fileType,
            body: toBase64(result.body),
        })
    },
    {
        pattern: /document\/\d+\/edit\/xml/,
        encode: values => {
            return EventMapper.bodyResult(
                omit(['result'], {
                    ...values,
                    xmlbody: path(['result', 'xmlbody'], values),
                })
            );
        }
    },
    {
        pattern: /organization\/\d+\/department/,
        decode: departmentMapper
    },
    {
        pattern: /kadrsyst\/legal\/\d+\/department/,
        decode: departmentMapper
    },
    {
        pattern: /project\/mass\/\d+$/,
        decode: WorksheetMassMapper
    },
    {
        pattern: /menu\/people\/state/,
        encode: personStateMapper,
    },
    {
        pattern: /notdone/,
        decode: notdoneMapper,
    },
    {
        pattern: /event\/types/,
        decode: evolve({
            result: field => ({
                ...field,
                shortResultBlock: field.ext,
                minSymbols: field.length,
            }),
        })
    },
    {
        pattern: /action\/application\/count/,
        decode: applicationApprovalCountersMapper
    },
    {
        pattern: /course\/\d+$/,
        decode: evolve({
            valuta: (item) => item.replace('!', ''),
            createData: formatDate
        })
    },
    {
        pattern: /timesheet/,
        decode: timetableMapper
    },
    {
        pattern: /action\/\d+$/,
        decode: stagesMapper
    },
    {
        pattern: /scenario\/data/,
        decode: stagesMapper
    },
    {
        pattern: /news\/\d+$/,
        decode: evolve({
            newsData: {
                body: decodeBase64Win1251
            }
        })
    },
    {
        pattern: /metro\/position\/dictionary$/,
        decode: evolve({
            salaryLevel: toNumber,
            salaryLevelMid: toNumber,
            northVacationCode: toNumber,
        }),
    },
    {
        pattern: /metro\/request(\/\d+)?$/,
        decode: evolve({
            salaryLevel: toNumber,
            salaryLevelMid: toNumber,
            initiatorCode: toNumber,
            HRGeneralistCode: toNumber,
            HRBPCode: toNumber,
            HRBPOperationsCode: toNumber,
            recruiterCode: toNumber,
            HRMCode: toNumber,
            chiefCode: toNumber,
            chiefPositionCode: toNumber,
            positionCodeForRestore: toNumber,
            displacedEmployeeCode: toCode,
        }),
        encode: evolve({
            salaryLevel: toNumber,
            salaryLevelMid: toNumber,

            positionCode: toNumber,
            positionCodeForRestore: toNumber,
            oldPositionId: toNumber,
            cellId: toNumber,
            cityCode: toNumber,
            regionCode: toNumber,
            departmentCode: toNumber,
            storeCode: toNumber,
            directorateCode: toNumber,
            unitCode: toNumber,
            divisionCode: toNumber,
            initiatorCode: toNumber,
            chartOfWorkCode: toNumber,
            occupationTypeCode: toNumber,
            displacedEmployeeCode: toNumber,
            recruiterCode: toNumber,
            positionGradeCode: toNumber,
            positionLevelCode: toNumber,
            bonusSchemeCode: toNumber,
            bonusSchemePercentCode: toNumber,
            familyDMSCode: toNumber,
            travellingWorkCode: toNumber,
            liabilityContractCode: toNumber,
            positionBudgetedCode: toNumber,
            approvalCode: toNumber,
            directorateDecisionCode: toNumber,
            directorateCouncilDate: toNumber,
            MGLHRGeneralistCode: toNumber,
            HQHRGeneralistCode: toNumber,
            HQHRBPCode: toNumber,
            HRBPOperationsCode: toNumber,
            HQCode: toNumber,
            positionIsInCatalogCode: toNumber,
            positionIsInOrgstrCode: toNumber,
            positionIsInSAPCode: toNumber,
            MoveToCnBCode: toNumber,
            positionDateFrom: toNumber,
            positionDateTo: toNumber,
            accessToPCCode: toNumber,
            companyCarCode: toNumber,
            chiefCode: toNumber,
            chiefPositionCode: toNumber,
            HRMCode: toNumber,
            HRGeneralistCode: toNumber,
            HRBPCode: toNumber,
            mobilePhoneCode: toNumber,

            comments: {
                date: toNumber,
            },
        }),
    },
    {
        pattern: /metro\/dictionary\/MetroCompetence/,
        decode: competenceMapper
    },
];

export default FORMATTERS;
